import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Card,
  Checkbox,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Table,
  Typography,
  message,
  Popconfirm,
} from "antd";
import {
  createWholesaler,
  deleteWholesaler,
  getWholesaleReferenceData,
  getWholesaleSettings,
  getWholesalers,
  saveWholesaleSettings,
  updateWholesaler,
  WholesalerReferenceData,
} from "../../../api/Services";
import styles from "./AdminWholesalers.module.scss";

const { Title, Text } = Typography;

const MODES = ["BRANCH_OFFICE", "SALE_POINT", "CLIENT_ID", "CLIENT_RUT"] as const;

type WholesalerRule = { id?: number; ruleType: string; matchValue: string };
type Wholesaler = {
  id: number;
  name: string;
  externalId: string;
  enabled: boolean;
  rules: WholesalerRule[];
};

const RuleValueField: React.FC<{
  ruleType: string;
  value?: string;
  onChange: (v: string) => void;
  reference: WholesalerReferenceData | null;
}> = ({ ruleType, value, onChange, reference }) => {
  const { t } = useTranslation();

  if (ruleType === "BRANCH_OFFICE") {
    return (
      <Select
        showSearch
        allowClear
        placeholder={t("Wholesalers-SelectBranchOffice")}
        style={{ width: 280 }}
        value={value || undefined}
        optionFilterProp="label"
        onChange={(v) => onChange(v != null ? String(v) : "")}
        options={(reference?.branchOffices ?? []).map((o) => ({
          value: String(o.id),
          label: o.secondary ? `${o.label} (${o.secondary})` : o.label,
        }))}
      />
    );
  }

  if (ruleType === "SALE_POINT") {
    return (
      <Select
        showSearch
        allowClear
        placeholder={t("Wholesalers-SelectSalePoint")}
        style={{ width: 280 }}
        value={value || undefined}
        optionFilterProp="label"
        onChange={(v) => onChange(v != null ? String(v) : "")}
        options={(reference?.salePoints ?? []).map((o) => ({
          value: String(o.id),
          label: o.secondary ? `${o.label} — ${o.secondary}` : o.label,
        }))}
      />
    );
  }

  if (ruleType === "CLIENT_ID") {
    return (
      <Select
        showSearch
        allowClear
        placeholder={t("Wholesalers-SelectClient")}
        style={{ width: 280 }}
        value={value || undefined}
        optionFilterProp="label"
        onChange={(v) => onChange(v != null ? String(v) : "")}
        options={(reference?.clients ?? []).map((o) => ({
          value: String(o.id),
          label: o.secondary ? `${o.label} — ${o.secondary}` : o.label,
        }))}
      />
    );
  }

  return (
    <Input
      placeholder={t("Wholesalers-EnterRut")}
      style={{ width: 280 }}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
  );
};

const AdminWholesalers: React.FC = () => {
  const { t } = useTranslation();
  const [items, setItems] = useState<Wholesaler[]>([]);
  const [modes, setModes] = useState<string[]>([]);
  const [reference, setReference] = useState<WholesalerReferenceData | null>(null);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Wholesaler | null>(null);
  const [form] = Form.useForm();

  const load = useCallback(async () => {
    setLoading(true);
    const [list, settings, ref] = await Promise.all([
      getWholesalers(),
      getWholesaleSettings(),
      getWholesaleReferenceData(),
    ]);
    setItems((list as Wholesaler[]) ?? []);
    setModes(settings?.identificationModes ?? []);
    setReference(ref);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const enabledModes = MODES.filter((m) => modes.includes(m));

  const openCreate = () => {
    if (enabledModes.length === 0) {
      message.warning(t("Wholesalers-SettingsTitle"));
      return;
    }
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({
      enabled: true,
      rules: [{ ruleType: enabledModes[0], matchValue: "" }],
    });
    setModalOpen(true);
  };

  const openEdit = (row: Wholesaler) => {
    setEditing(row);
    form.setFieldsValue({
      name: row.name,
      externalId: row.externalId,
      enabled: row.enabled,
      rules:
        row.rules.length > 0
          ? row.rules
          : [{ ruleType: enabledModes[0] ?? "BRANCH_OFFICE", matchValue: "" }],
    });
    setModalOpen(true);
  };

  const saveWholesaler = async () => {
    const values = await form.validateFields();
    const body = {
      name: values.name,
      externalId: values.externalId,
      enabled: values.enabled !== false,
      rules: (values.rules ?? []).filter(
        (r: WholesalerRule) => r?.ruleType && r?.matchValue?.trim()
      ),
    };
    if (editing) {
      await updateWholesaler(editing.id, body);
    } else {
      await createWholesaler(body);
    }
    message.success("OK");
    setModalOpen(false);
    load();
  };

  const saveModes = async () => {
    await saveWholesaleSettings(modes);
    message.success(t("Wholesalers-SettingsSaved"));
    load();
  };

  return (
    <div className={styles.wrapper}>
      <Title level={3}>{t("Wholesalers-PageTitle")}</Title>

      <Card className={styles.card} title={t("Wholesalers-SettingsTitle")}>
        <Checkbox.Group
          value={modes}
          onChange={(v) => setModes(v as string[])}
          className={styles.modeGroup}
        >
          {MODES.map((mode) => (
            <Checkbox key={mode} value={mode}>
              {t(`Wholesalers-Mode-${mode}`)}
            </Checkbox>
          ))}
        </Checkbox.Group>
        <Button type="primary" onClick={saveModes} style={{ marginTop: 12 }}>
          {t("Wholesalers-SaveSettings")}
        </Button>
      </Card>

      <Card
        className={styles.card}
        title={t("Wholesalers-PageTitle")}
        extra={
          <Button type="primary" onClick={openCreate} disabled={enabledModes.length === 0}>
            {t("Wholesalers-Add")}
          </Button>
        }
      >
        <Table
          loading={loading}
          dataSource={items.map((r) => ({ ...r, key: r.id }))}
          pagination={false}
          columns={[
            { title: t("Wholesalers-Column-Name"), dataIndex: "name" },
            { title: t("Wholesalers-Column-ExternalId"), dataIndex: "externalId" },
            {
              title: t("Wholesalers-Column-Rules"),
              key: "rules",
              render: (_: unknown, row: Wholesaler) =>
                row.rules
                  .map((r) => {
                    const label = resolveRuleLabel(r, reference, t);
                    return `${t(`Wholesalers-Mode-${r.ruleType}`)}: ${label}`;
                  })
                  .join(" · "),
            },
            {
              title: "",
              key: "actions",
              render: (_: unknown, row: Wholesaler) => (
                <Space>
                  <Button size="small" onClick={() => openEdit(row)}>
                    {t("Wholesalers-Edit")}
                  </Button>
                  <Popconfirm
                    title={t("Wholesalers-DeleteConfirm")}
                    onConfirm={async () => {
                      await deleteWholesaler(row.id);
                      load();
                    }}
                  >
                    <Button size="small" danger>
                      Eliminar
                    </Button>
                  </Popconfirm>
                </Space>
              ),
            },
          ]}
        />
      </Card>

      <Modal
        visible={modalOpen}
        title={editing ? t("Wholesalers-Edit") : t("Wholesalers-Add")}
        onCancel={() => setModalOpen(false)}
        onOk={saveWholesaler}
        width={720}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label={t("Wholesalers-Column-Name")} rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item
            name="externalId"
            label={t("Wholesalers-Column-ExternalId")}
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="enabled" valuePropName="checked" initialValue>
            <Checkbox>{t("Wholesalers-Active")}</Checkbox>
          </Form.Item>
          <Text type="secondary">{t("Wholesalers-RulesHint")}</Text>
          <Form.List name="rules">
            {(fields, { add, remove }) => (
              <>
                {fields.map((field) => (
                  <div key={field.key} className={styles.ruleRow}>
                    <Form.Item
                      {...field}
                      name={[field.name, "ruleType"]}
                      rules={[{ required: true }]}
                      className={styles.ruleTypeField}
                    >
                      <Select
                        style={{ width: 200 }}
                        options={enabledModes.map((m) => ({
                          value: m,
                          label: t(`Wholesalers-Mode-${m}`),
                        }))}
                      />
                    </Form.Item>
                    <Form.Item shouldUpdate noStyle>
                      {() => {
                        const ruleType = form.getFieldValue([
                          "rules",
                          field.name,
                          "ruleType",
                        ]);
                        const matchValue = form.getFieldValue([
                          "rules",
                          field.name,
                          "matchValue",
                        ]);
                        return (
                          <Form.Item
                            {...field}
                            name={[field.name, "matchValue"]}
                            rules={[{ required: true }]}
                            className={styles.ruleValueField}
                          >
                            <RuleValueField
                              ruleType={ruleType}
                              value={matchValue}
                              reference={reference}
                              onChange={(v) => {
                                const rules = form.getFieldValue("rules") ?? [];
                                rules[field.name] = {
                                  ...rules[field.name],
                                  matchValue: v,
                                };
                                form.setFieldsValue({ rules });
                              }}
                            />
                          </Form.Item>
                        );
                      }}
                    </Form.Item>
                    <Button type="link" onClick={() => remove(field.name)}>
                      Quitar
                    </Button>
                  </div>
                ))}
                <Button
                  type="dashed"
                  onClick={() =>
                    add({
                      ruleType: enabledModes[0] ?? "BRANCH_OFFICE",
                      matchValue: "",
                    })
                  }
                  block
                >
                  {t("Wholesalers-AddRule")}
                </Button>
              </>
            )}
          </Form.List>
        </Form>
      </Modal>
    </div>
  );
};

function resolveRuleLabel(
  rule: WholesalerRule,
  reference: WholesalerReferenceData | null,
  t: (key: string) => string
): string {
  if (!reference) return rule.matchValue;
  const id = Number(rule.matchValue);
  if (rule.ruleType === "BRANCH_OFFICE") {
    const hit = reference.branchOffices.find((o) => o.id === id);
    return hit?.label ?? rule.matchValue;
  }
  if (rule.ruleType === "SALE_POINT") {
    const hit = reference.salePoints.find((o) => o.id === id);
    return hit ? `${hit.label}${hit.secondary ? ` (${hit.secondary})` : ""}` : rule.matchValue;
  }
  if (rule.ruleType === "CLIENT_ID") {
    const hit = reference.clients.find((o) => o.id === id);
    return hit?.label ?? rule.matchValue;
  }
  return rule.matchValue;
}

export default AdminWholesalers;
